import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Briefcase, TrendingUp, Repeat, Pencil, Play, Pause, StopCircle, Plus,
  Activity, Stethoscope, Heart, Pill, Loader2
} from 'lucide-react';
import { formatDate } from '../../lib/utils';

const getServicoIcon = (nome) => {
  const nameLower = nome?.toLowerCase() || '';
  if (nameLower.includes('fisio')) return <Activity className="h-4 w-4 text-emerald-600" />;
  if (nameLower.includes('medico')) return <Stethoscope className="h-4 w-4 text-blue-600" />;
  if (nameLower.includes('enferma')) return <Heart className="h-4 w-4 text-red-500" />;
  if (nameLower.includes('medica')) return <Pill className="h-4 w-4 text-purple-600" />;
  return <Briefcase className="h-4 w-4 text-gray-600" />;
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

export const TabServicos = ({
  servicosPaciente,
  servicosCatalogo,
  resumoCustos,
  resumoPeriodo,
  setResumoPeriodo,
  loadingResumo,
  canViewServiceRevenue,
  canViewServicePayout,
  canViewStockCosts,
  showServicoModal,
  setShowServicoModal,
  editingServico,
  servicoForm,
  setServicoForm,
  handleAddServico,
  handleEditServico,
  handlePauseServico,
  handleEndServico,
  resetServicoForm
}) => (
  <>
    <TabsContent value="servicos">
      {/* Painel de Resumo de Custos */}
      <Card className="mb-6 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 border-slate-200 dark:border-slate-700">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Resumo de Custos
            </CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground">Período:</Label>
              <Select value={resumoPeriodo.toString()} onValueChange={(v) => setResumoPeriodo(parseInt(v))}>
                <SelectTrigger className="w-[140px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 dias</SelectItem>
                  <SelectItem value="15">15 dias</SelectItem>
                  <SelectItem value="30">30 dias</SelectItem>
                  <SelectItem value="60">60 dias</SelectItem>
                  <SelectItem value="90">90 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loadingResumo ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : resumoCustos ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {canViewServiceRevenue && (
              <div className="p-3 rounded-lg bg-white dark:bg-slate-800 border">
                <p className="text-xs text-muted-foreground mb-1">Receita Total</p>
                <p className="text-xl font-bold text-green-600">
                  {formatCurrency(resumoCustos.resumo.total_receita)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatCurrency(resumoCustos.resumo.receita_dia)}/dia
                </p>
              </div>
              )}
              {canViewServicePayout && (
              <div className="p-3 rounded-lg bg-white dark:bg-slate-800 border">
                <p className="text-xs text-muted-foreground mb-1">Custo Total</p>
                <p className="text-xl font-bold text-red-600">
                  {formatCurrency(resumoCustos.resumo.total_custo)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatCurrency(resumoCustos.resumo.custo_dia)}/dia
                </p>
              </div>
              )}
              {canViewServiceRevenue && canViewServicePayout && (
              <div className="p-3 rounded-lg bg-white dark:bg-slate-800 border">
                <p className="text-xs text-muted-foreground mb-1">Margem</p>
                <p className={`text-xl font-bold ${(resumoCustos.resumo.margem || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(resumoCustos.resumo.margem)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {resumoCustos.resumo.margem_percentual}% de margem
                </p>
              </div>
              )}
              <div className="p-3 rounded-lg bg-white dark:bg-slate-800 border">
                <p className="text-xs text-muted-foreground mb-1">MEDICAÇÃO</p>
                <p className="text-xl font-bold text-blue-600">
                  {resumoCustos.medicacao?.ocorrencias_periodo || 0} un
                </p>
                <p className="text-xs text-muted-foreground">
                  {resumoCustos.medicacao?.frequencia || '-'}
                </p>
              </div>
              {!(canViewServiceRevenue || canViewServicePayout) && (
                <div className="col-span-2 p-3 rounded-lg bg-muted/50 border flex items-center justify-center">
                  <p className="text-sm text-muted-foreground">Valor restrito ao administrador master.</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">
              Nenhum dado disponível
            </p>
          )}
          
          {/* Breakdown details */}
          {resumoCustos && (canViewServiceRevenue || canViewServicePayout || canViewStockCosts) && (resumoCustos.servicos?.length > 0 || resumoCustos.consumo_estoque?.itens?.length > 0) && (
            <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-2 gap-4">
              {canViewServiceRevenue && resumoCustos.servicos?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 text-muted-foreground">Serviços ({resumoPeriodo} dias)</h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {resumoCustos.servicos.map((s, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="truncate flex-1">{s.nome}</span>
                        <span className="text-green-600 ml-2">{formatCurrency(s.receita)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {canViewStockCosts && resumoCustos.consumo_estoque?.itens?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 text-muted-foreground">Consumo Estoque ({resumoPeriodo} dias)</h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {resumoCustos.consumo_estoque.itens.map((item, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="truncate flex-1">{item.item_nome}</span>
                        <span className="text-red-600 ml-2">{formatCurrency(item.custo_total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Serviços Profissionais
              </CardTitle>
              <CardDescription>
                Serviços contratados para esta internação (fisioterapia, médico, enfermagem, etc.)
              </CardDescription>
            </div>
            <Button onClick={() => { resetServicoForm(); setShowServicoModal(true); }} data-testid="btn-adicionar-servico">
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Serviço
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {servicosPaciente.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Briefcase className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum serviço contratado</p>
              <p className="text-sm">Adicione serviços profissionais para esta internação</p>
            </div>
          ) : (
            <div className="border rounded-md overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Serviço</th>
                    <th className="text-left p-3 font-medium">Profissional</th>
                    <th className="text-center p-3 font-medium">Frequência</th>
                    <th className="text-right p-3 font-medium">{canViewServiceRevenue ? 'Valor Recebido' : '—'}</th>
                    <th className="text-right p-3 font-medium">{canViewServicePayout ? 'Valor Pago' : '—'}</th>
                    <th className="text-center p-3 font-medium">Status</th>
                    <th className="text-center p-3 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {servicosPaciente.map(servico => (
                    <tr key={servico.id || `virtual-${servico.servico_nome}`} className={`border-t ${servico.is_computed ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {getServicoIcon(servico.servico_nome)}
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{servico.servico_nome}</span>
                              {servico.is_computed && (
                                <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                                  automático
                                </Badge>
                              )}
                              {servico.is_default && (
                                <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600">
                                  padrão
                                </Badge>
                              )}
                            </div>
                            {servico.servico_nome === 'MEDICAÇÃO' && servico.consumo_items && servico.consumo_items.length > 0 && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {servico.consumo_items.slice(0, 3).map((item, idx) => (
                                  <span key={idx}>
                                    {item.item_nome} ({item.aplicacoes_por_dia}x)
                                    {idx < Math.min(servico.consumo_items.length, 3) - 1 ? ', ' : ''}
                                  </span>
                                ))}
                                {servico.consumo_items.length > 3 && <span> +{servico.consumo_items.length - 3} mais</span>}
                              </div>
                            )}
                          </div>
                        </div>
                        {servico.observacoes && !servico.is_computed && (
                          <p className="text-xs text-muted-foreground mt-1">{servico.observacoes}</p>
                        )}
                      </td>
                      <td className="p-3">
                        {servico.profissional || <span className="text-muted-foreground">-</span>}
                      </td>
                      <td className="p-3 text-center">
                        {servico.servico_nome === 'MEDICAÇÃO' && servico.unidades_por_dia ? (
                          <div className="flex flex-col items-center">
                            <span className="text-lg font-bold text-blue-600">
                              {servico.unidades_por_dia}/dia
                            </span>
                            {servico.manual_override && (
                              <Badge variant="outline" className="text-xs mt-1 bg-amber-100 text-amber-700">
                                manual
                              </Badge>
                            )}
                          </div>
                        ) : servico.recorrente ? (
                          <div className="flex flex-col items-center gap-1">
                            <div className="flex items-center justify-center gap-1">
                              <Repeat className="h-3 w-3 text-blue-500" />
                              <span className="text-sm">
                                {servico.frequencia_dias === 1 
                                  ? 'Diário' 
                                  : servico.frequencia_dias === 2
                                  ? '3x/semana'
                                  : servico.frequencia_dias === 7
                                  ? '1x/semana'
                                  : `A cada ${servico.frequencia_dias} dias`}
                              </span>
                            </div>
                            {servico.quantidade_por_ocorrencia > 1 && (
                              <span className="text-xs text-muted-foreground">
                                {servico.quantidade_por_ocorrencia}x por vez
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-1">
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                              Único
                            </Badge>
                            <span className="text-sm font-medium">
                              {servico.quantidade_por_ocorrencia || 1}x
                            </span>
                            {servico.data_realizacao && (
                              <span className="text-xs text-muted-foreground">
                                {formatDate(servico.data_realizacao)}
                              </span>
                            )}
                            {!servico.data_realizacao && servico.data_inicio && (
                              <span className="text-xs text-muted-foreground">
                                {formatDate(servico.data_inicio)}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        {canViewServiceRevenue ? (
                          servico.servico_nome === 'MEDICAÇÃO' && servico.valor_receita_diaria ? (
                            <div className="flex flex-col items-end">
                              <span className="text-green-600 font-medium">
                                {formatCurrency(servico.valor_receita_diaria)}/dia
                              </span>
                              <span className="text-xs text-muted-foreground">
                                ({formatCurrency(servico.servico_recebido_valor)}/un)
                              </span>
                            </div>
                          ) : (
                            <span className="text-green-600 font-medium">
                              {formatCurrency(servico.servico_recebido_valor)}
                            </span>
                          )
                        ) : (
                          <span className="text-muted-foreground" title="Valor restrito ao administrador master.">—</span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        {canViewServicePayout ? (
                          <span className="text-red-600 font-medium">
                            {formatCurrency(servico.servico_pago_valor || 0)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground" title="Valor restrito ao administrador master.">—</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant={
                          servico.status === 'ativo' ? 'default' :
                          servico.status === 'pausado' ? 'secondary' : 'outline'
                        } className={
                          servico.status === 'ativo' ? 'bg-green-100 text-green-800' :
                          servico.status === 'pausado' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {servico.status === 'ativo' ? 'Ativo' :
                           servico.status === 'pausado' ? 'Pausado' : 'Encerrado'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex justify-center gap-1">
                          {servico.status !== 'encerrado' && !servico.is_virtual && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleEditServico(servico)}
                                title="Editar"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handlePauseServico(servico.id, servico.status)}
                                title={servico.status === 'pausado' ? 'Reativar' : 'Pausar'}
                              >
                                {servico.status === 'pausado' ? (
                                  <Play className="h-4 w-4 text-green-600" />
                                ) : (
                                  <Pause className="h-4 w-4 text-yellow-600" />
                                )}
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleEndServico(servico.id)}
                                title="Encerrar"
                              >
                                <StopCircle className="h-4 w-4 text-red-500" />
                              </Button>
                            </>
                          )}
                          {servico.is_virtual && (
                            <span className="text-xs text-muted-foreground">
                              Via Consumo
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>

    {/* Serviço Modal */}
    <Dialog open={showServicoModal} onOpenChange={setShowServicoModal}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {editingServico ? 'Editar Serviço' : 'Adicionar Serviço'}
          </DialogTitle>
          <DialogDescription>
            Configure um serviço profissional para esta internação
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Serviço *</Label>
            <Select
              value={servicoForm.servico_id}
              onValueChange={(value) => setServicoForm({...servicoForm, servico_id: value})}
              disabled={!!editingServico}
            >
              <SelectTrigger data-testid="select-servico">
                <SelectValue placeholder="Selecione um serviço" />
              </SelectTrigger>
              <SelectContent>
                {servicosCatalogo.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    <div className="flex items-center gap-2">
                      {getServicoIcon(s.nome)}
                      <span>{s.nome}</span>
                      {canViewServiceRevenue && s.recebido_valor > 0 && (
                        <span className="text-xs text-green-600 ml-2">
                          {formatCurrency(s.recebido_valor)}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Profissional Responsável</Label>
            <Input
              value={servicoForm.profissional}
              onChange={(e) => setServicoForm({...servicoForm, profissional: e.target.value})}
              placeholder="Nome do profissional (opcional)"
              data-testid="input-profissional"
            />
          </div>

          <div className="p-4 border rounded-lg bg-muted/30 space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="flex items-center gap-2">
                  <Repeat className="h-4 w-4 text-muted-foreground" />
                  Serviço Recorrente
                </Label>
                <p className="text-sm text-muted-foreground">
                  {servicoForm.recorrente 
                    ? 'Repete automaticamente conforme frequência'
                    : 'Serviço único (pontual)'}
                </p>
              </div>
              <Switch
                checked={servicoForm.recorrente}
                onCheckedChange={(checked) => setServicoForm({...servicoForm, recorrente: checked})}
                data-testid="switch-recorrente"
              />
            </div>

            {servicoForm.recorrente ? (
              <div className="grid grid-cols-2 gap-4 pt-3 border-t">
                <div className="space-y-2">
                  <Label>Frequência</Label>
                  <Select
                    value={servicoForm.frequencia_dias.toString()}
                    onValueChange={(value) => setServicoForm({...servicoForm, frequencia_dias: value})}
                  >
                    <SelectTrigger data-testid="select-frequencia">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Diário</SelectItem>
                      <SelectItem value="2">A cada 2 dias (3x/semana)</SelectItem>
                      <SelectItem value="3">A cada 3 dias</SelectItem>
                      <SelectItem value="7">Semanal</SelectItem>
                      <SelectItem value="14">Quinzenal</SelectItem>
                      <SelectItem value="30">Mensal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Qtd por Ocorrência</Label>
                  <Input
                    type="number"
                    min="1"
                    value={servicoForm.quantidade_por_ocorrencia}
                    onChange={(e) => setServicoForm({...servicoForm, quantidade_por_ocorrencia: e.target.value})}
                    data-testid="input-qtd-ocorrencia"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 pt-3 border-t">
                <div className="space-y-2">
                  <Label>Quantidade</Label>
                  <Input
                    type="number"
                    min="1"
                    value={servicoForm.quantidade_por_ocorrencia}
                    onChange={(e) => setServicoForm({...servicoForm, quantidade_por_ocorrencia: e.target.value})}
                    placeholder="1"
                    data-testid="input-quantidade-unica"
                  />
                  <p className="text-xs text-muted-foreground">
                    Quantidade total do serviço
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Data de Realização</Label>
                  <Input
                    type="date"
                    value={servicoForm.data_realizacao || servicoForm.data_inicio}
                    onChange={(e) => setServicoForm({...servicoForm, data_realizacao: e.target.value, data_inicio: e.target.value})}
                    data-testid="input-data-realizacao"
                  />
                  <p className="text-xs text-muted-foreground">
                    Data em que o serviço será/foi realizado
                  </p>
                </div>
              </div>
            )}
          </div>

          {servicoForm.recorrente && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Início *</Label>
                <Input
                  type="date"
                  value={servicoForm.data_inicio}
                  onChange={(e) => setServicoForm({...servicoForm, data_inicio: e.target.value})}
                  disabled={!!editingServico}
                  data-testid="input-data-inicio"
                />
              </div>
              <div className="space-y-2">
                <Label>Data Fim (opcional)</Label>
                <Input
                  type="date"
                  value={servicoForm.data_fim}
                  onChange={(e) => setServicoForm({...servicoForm, data_fim: e.target.value})}
                  data-testid="input-data-fim"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea
              value={servicoForm.observacoes}
              onChange={(e) => setServicoForm({...servicoForm, observacoes: e.target.value})}
              placeholder="Observações sobre o serviço..."
              rows={2}
              data-testid="input-observacoes-servico"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowServicoModal(false)}>Cancelar</Button>
          <Button 
            onClick={handleAddServico}
            disabled={!servicoForm.servico_id || !servicoForm.data_inicio}
            data-testid="btn-salvar-servico"
          >
            {editingServico ? 'Salvar' : 'Adicionar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
);
